import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FileModel } from '../../../../shared/file/file.models';
import { MatDialog } from '@angular/material/dialog';
import { DashboardNotificationService } from '../../../../shared/notification/notification.service';
import { FileSizePipe } from '../../../../shared/file/file-size.pipe';
import { FileViewComponent } from '../file-view/file-view.component';

@Component({
  standalone: false,
  selector: 'app-csv-upload',
  templateUrl: './csv-upload.component.html',
  styleUrls: ['./csv-upload.component.scss']
})
export class CsvUploadComponent {
  private DEFAULT_MAX_FILES_COUNT = 1;
  private DEFAULT_MAX_FILE_SIZE = 20 * 1024 * 1024;

  @Input()
  get files(): FileModel[] {
    return this.filesValue;
  }
  set files(value: FileModel[]) {
    this.filesValue = [...value];
  }
  filesValue: FileModel[] = [];

  @Input()
  placeholder?: string;

  @Input()
  isViewOnly = false;

  @Input()
  maxFileSize?: number;

  @Input()
  maxFilesCount?: number;

  @Input()
  accept = '.csv';

  @Output()
  filesChange = new EventEmitter<FileModel[]>();

  get uploadAvailable() {
    return this.maxFilesCount ?
      this.files.length < this.maxFilesCount :
      this.files.length < this.DEFAULT_MAX_FILES_COUNT;
  }

  constructor(
    private dialog: MatDialog,
    private notificationService: DashboardNotificationService,
    private fileSizePipe: FileSizePipe) {
  }

  isEmpty() {
    return this.files.length === 0;
  }

  onFileInputChange(event: any) {
    const eventFiles = event.target.files as FileList;
    if (!this.allEventFilesValid(eventFiles)) {
      return;
    }

    this.pushFiles(eventFiles);
  }

  onDragAndDropFile(event: any) {
    const eventFiles = event as FileList;
    if (!this.allEventFilesValid(event)) {
      return;
    }

    this.pushFiles(eventFiles);
  }

  onReplaceFile(file: FileModel, event: any) {
    if (!file) {
      return;
    }

    const eventFiles = event.target.files as FileList;
    if (!this.allEventFilesValid(eventFiles, true)) {
      return;
    }

    this.detectUrl(eventFiles[0])
      .then(url => {
        if (url) {
          const fileIndex = this.files.findIndex(x => x === file);
          this.files[fileIndex] = {
            url: url,
            name: eventFiles[0].name
          };
          this.filesChange.emit(this.files);
        }
      });
  }

  onDeleteFile(file: FileModel) {
    if (!file) {
      return;
    }
    const fileIndex = this.files.findIndex(x => x === file);
    if (fileIndex > -1) {
      this.files.splice(fileIndex, 1)
    }
    this.filesChange.emit(this.files);
  }

  onFileInputClick(event: any) {
    event.target.value = null;
  }


  isViewAvailable(file: FileModel) {
    return FileModel.isImage(file.name!) || FileModel.isPdf(file.name!);
  }

  private allEventFilesValid(eventFiles: FileList, isReplace?: boolean): boolean {
    if (!eventFiles || eventFiles.length < 1) {
      return false;
    }

    const availableFilesCount = this.files.length + eventFiles.length;
    const maxFilesCount = this.maxFilesCount || this.DEFAULT_MAX_FILES_COUNT;

    if (availableFilesCount > maxFilesCount && !isReplace) {
      this.notificationService.error(`The maximum number of files is ${maxFilesCount}`);
      return false;
    }

    for (let i = 0; i < eventFiles.length; i++) {
      const fileSize = eventFiles[i].size;
      const maxFileSize = this.maxFileSize || this.DEFAULT_MAX_FILE_SIZE;
      if (fileSize > maxFileSize) {
        const errorMessage = `File size cannot be greater than ${this.fileSizePipe.transform(maxFileSize)}`;
        this.notificationService.error(errorMessage);
        return false;
      }
    }
    return true;
  }

  private detectUrl(file: File) {
    return new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        resolve(reader.result as string);
      };
    });
  }

  private pushFiles(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      this.detectUrl(files[i])
        .then(url => {
          if (url) {
            const file: FileModel = {
              url: url,
              name: files[i].name
            };

            this.files.push(file);
            this.filesChange.emit(this.files);
          }
        });
    }
  }

  onOpenTemplate() {
    const example = {
      name: 'CsvExample',
      url: './assets/images/csv-batch-example.jpg'
    } as FileModel

    this.dialog.open(FileViewComponent, {
      panelClass: 'qira-file-view-dialog',
      maxWidth: 'calc(100vw - 32px)',
      maxHeight: 'calc(100vh - 32px)',
      autoFocus: false,
      data: example
    });
  }
}
